import { useState, useEffect } from "react";
import { QuizQuestion } from "../../types/api";
import { Trash2, TrashIcon } from "lucide-react";

interface QuizFormProps {
  questions?: QuizQuestion[];
  initialQuiz?: QuizQuestion[];
  mode?: string;
  onOptionSelect?: (questionId: number, selectedOption: number) => void;
  selectedAnswers?: { questionId: number; selectedOption: number }[];
  onQuizUpdate?: (quiz: QuizQuestion[]) => void;
  viewMode?: boolean;
}

const QuizForm: React.FC<QuizFormProps> = ({
  questions = [],
  initialQuiz = [],
  mode = "edit",
  onOptionSelect,
  selectedAnswers = [],
  onQuizUpdate,
  viewMode = false,
}) => {
  const [localQuestions, setQuestions] = useState<QuizQuestion[]>(
    questions.length > 0
      ? questions
      : initialQuiz.length > 0
        ? initialQuiz
        : [
            {
              id: 1,
              question: "",
              options: [
                { text: "", isCorrect: false },
                { text: "", isCorrect: false },
                { text: "", isCorrect: false },
                { text: "", isCorrect: false },
              ],
            },
          ],
  );

  useEffect(() => {
    if (questions.length > 0) {
      setQuestions(questions);
    } else if (initialQuiz.length > 0) {
      setQuestions(initialQuiz);
    }
  }, [questions, initialQuiz]);

  const addQuestion = () => {
    if (viewMode || mode === "view") return;
    const newQuestion: QuizQuestion = {
      id: localQuestions.length + 1,
      question: "",
      options: [
        { text: "", isCorrect: false },
        { text: "", isCorrect: false },
        { text: "", isCorrect: false },
        { text: "", isCorrect: false },
      ],
    };
    const updatedQuestions = [...localQuestions, newQuestion];
    setQuestions(updatedQuestions);
    onQuizUpdate?.(updatedQuestions);
  };

  const deleteQuestion = (id: number) => {
    if (viewMode || mode === "view") return;
    const updatedQuestions = localQuestions.filter((q) => q.id !== id);
    setQuestions(updatedQuestions);
    onQuizUpdate?.(updatedQuestions);
  };

  const handleInputChange = (index: number, value: string) => {
    if (viewMode || mode === "view") return;
    const updatedQuestions = [...localQuestions];
    updatedQuestions[index].question = value;
    setQuestions(updatedQuestions);
    onQuizUpdate?.(updatedQuestions);
  };

  const handleOptionChange = (
    qIndex: number,
    oIndex: number,
    value: string,
  ) => {
    if (viewMode || mode === "view") return;
    const updatedQuestions = [...localQuestions];
    updatedQuestions[qIndex].options[oIndex] = {
      ...updatedQuestions[qIndex].options[oIndex],
      text: value,
    };
    setQuestions(updatedQuestions);
    onQuizUpdate?.(updatedQuestions);
  };

  const handleCorrectAnswer = (qIndex: number, oIndex: number) => {
    if (viewMode || mode === "view") return;
    const updatedQuestions = [...localQuestions];
    updatedQuestions[qIndex].options = updatedQuestions[qIndex].options.map(
      (opt, idx) => ({
        ...opt,
        isCorrect: idx === oIndex,
      }),
    );
    setQuestions(updatedQuestions);
    onQuizUpdate?.(updatedQuestions);
  };

  const handleOptionSelectInternal = (
    questionId: number,
    optionIndex: number,
  ) => {
    if (mode === "view" && onOptionSelect) {
      onOptionSelect(questionId, optionIndex);
    }
  };

  return (
    <div className="mx-auto p-6 bg-white shadow-md rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold mb-4"></h2>
        <p className="text-right text-orange-500 font-semibold text-sm mb-4">
          {/* {viewMode || mode === "view"
            ? "Viewing correct answers"
            : "Please select the radio button for the correct answer"} */}
        </p>
      </div>
      {localQuestions.map((q, qIndex) => (
        <div key={q.id} className="mb-6 p-4 hover:bg-blue-50 rounded-lg">
          <div className="flex gap-3 items-center mb-2">
            <h3 className="text-lg font-semibold">{qIndex + 1}) Question</h3>
            {localQuestions.length > 1 && !(viewMode || mode === "view") && (
              <button
                className="text-red-500 bg-red-100 p-2 rounded-full hover:bg-red-200"
                onClick={() => deleteQuestion(q.id)}
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
          <input
            type="text"
            className={`w-full p-2 border-2 border-[#a5b7cf] placeholder:text-[#6c7888] rounded mb-2 ${
              viewMode || mode === "view"
                ? "bg-gray-100 cursor-not-allowed"
                : ""
            }`}
            placeholder="Question"
            value={q.question}
            onChange={(e) => handleInputChange(qIndex, e.target.value)}
            disabled={viewMode || mode === "view"}
          />
          <div className="grid grid-cols-4 gap-4">
            {["A", "B", "C", "D"].map((label, oIndex) => (
              <div key={oIndex} className="flex items-center p-2">
                <span className="font-semibold bg-gray-200 px-3 py-2 rounded-tl rounded-bl flex items-center h-10">
                  {label}
                </span>
                <div className="relative flex-grow">
                  <input
                    type="text"
                    className={`w-full p-2 border-2 border-[#a5b7cf] rounded-tr rounded-br pr-8 h-10 placeholder:text-[#6c7888] ${
                      viewMode || mode === "view"
                        ? "bg-gray-100 cursor-not-allowed"
                        : ""
                    }`}
                    placeholder={`option-${oIndex + 1}`}
                    value={q.options[oIndex].text}
                    onChange={(e) =>
                      handleOptionChange(qIndex, oIndex, e.target.value)
                    }
                    disabled={viewMode || mode === "view"}
                  />
                  <input
                    type="radio"
                    name={`correct-${qIndex}`}
                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${
                      viewMode || mode === "view"
                        ? "cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    checked={
                      mode === "view"
                        ? selectedAnswers.find((a) => a.questionId === q.id)
                            ?.selectedOption === oIndex
                        : q.options[oIndex].isCorrect
                    }
                    onChange={() =>
                      mode === "view"
                        ? handleOptionSelectInternal(q.id, oIndex)
                        : handleCorrectAnswer(qIndex, oIndex)
                    }
                    disabled={viewMode && mode !== "view"}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {!(viewMode || mode === "view") && (
        <div className="flex justify-end">
          <button
            onClick={addQuestion}
            className="flex items-center gap-2 bg-[#2f64ac] text-white px-4 py-2 rounded-full hover:bg-[#254f8b]"
            disabled={viewMode || mode === "view"}
          >
            ➕ ADD QUESTION
          </button>
        </div>
      )}
    </div>
  );
};

export default QuizForm;
